import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {
  PlateCreationStructuredTable,
  PlateCreationTableMetadata,
  PlateCreationTableRowMetadata,
} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {ACTION_CREATE_PLATES} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  ServerSideWriteActionResult,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  isBatchingRequired,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {StructuredTableSubmissionScope} from '../StructuredTableApiConstants';
import {StructuredTableKind, TableActionFunctionParameters} from '../StructuredTableConstants';
import {
  getRowAwareLoadingMessage,
  RowSubmissionRange,
  getStructuredTableSubmissionScope,
} from './makeSubmitRowActionFunction';

export async function doPerformCreatePlates(
  entryOrTemplateId: string,
  structuredTable: PlateCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader,
  allRowsSubmitted?: boolean
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.CreatePlatesResponse>> {
  const submissionScope = getStructuredTableSubmissionScope(selection);
  const batchSize = getSubmissionBatchSize(StructuredTableKind.PLATE_CREATION);
  if (isBatchingRequired(computedTable, batchSize, selection)) {
    return await attemptBatchedCreatePlates(
      entryOrTemplateId,
      batchSize!,
      structuredTable,
      computedTable,
      actionFunctionContext,
      selection,
      submissionScope,
      updateStatus,
      dataModsHeader,
      allRowsSubmitted
    );
  } else {
    const pertinentComputedTable = selection
      ? computedTable.slice(selection.start, selection.end)
      : computedTable;

    const responseData = await attemptCreatePlates(
      entryOrTemplateId,
      structuredTable,
      pertinentComputedTable,
      actionFunctionContext,
      submissionScope,
      selection,
      undefined,
      dataModsHeader,
      allRowsSubmitted
    );
    return {resultType: ActionResultType.SUCCESS, responseData};
  }
}

async function attemptCreatePlates(
  entryOrTemplateId: string,
  structuredTable: PlateCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  submissionScope: StructuredTableSubmissionScope,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader,
  allRowsSubmitted?: boolean
): Promise<ActionFunctionDAO.CreatePlatesResponse> {
  return await ActionFunctionDAO.createPlates(
    entryOrTemplateId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    submissionScope,
    latestVersionId,
    dataModsHeader,
    allRowsSubmitted
  );
}

async function attemptBatchedCreatePlates(
  entryOrTemplateId: string,
  batchSize: number,
  structuredTable: PlateCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection: RowSubmissionRange | undefined,
  submissionScope: StructuredTableSubmissionScope,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader,
  allRowsSubmitted?: boolean
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.CreatePlatesResponse>> {
  let latestVersionId: number | undefined;
  let partialSuccess = false;
  let responseData: ActionFunctionDAO.CreatePlatesResponse = {entryVersionId: -1};
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  try {
    for (const {range, evaluatedRowBatch} of batches) {
      if (updateStatus) {
        updateStatus(getRowAwareLoadingMessage(range, 'Updating plates for rows', 'Updating plate for row'));
      }
      const batchResult = await attemptCreatePlates(
        entryOrTemplateId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        submissionScope,
        range,
        latestVersionId,
        dataModsHeader,
        allRowsSubmitted
      );
      latestVersionId = batchResult.entryVersionId;
      responseData = {entryVersionId: latestVersionId};
      submittedRange = {...submittedRange, end: range.end};
      partialSuccess = true;
    }
  } catch (e) {
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }

    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function onInvokeCreatePlatesActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<PlateCreationTableMetadata, PlateCreationTableRowMetadata>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    'Create plates action is only supported in entries.',
    {apiId: entryOrTemplateId}
  );

  await performServerSideTableWriteActionWithAudits({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Updating plates...',
    autoSaverAction: ACTION_CREATE_PLATES,
    doPerformAction: ({
      updateStatus,
      dataModsHeader,
      allRowsSubmitted,
    }): Promise<ServerSideWriteActionResult<ActionFunctionDAO.CreatePlatesResponse>> =>
      doPerformCreatePlates(
        entryOrTemplateId,
        structuredTable,
        computedTable,
        actionFunctionContext,
        undefined,
        updateStatus,
        dataModsHeader,
        allRowsSubmitted
      ),
    analyticsActionForPerfTracking: 'plate_creation_table_create',
    structuredTables: [structuredTable],
    structuredTableAdapters: [structuredTableAdapter],
    computedTables: [computedTable],
    doPostRefresh: (actionResult) => {
      invokeStructuredTableMappings(
        entryOrTemplateId,
        structuredTable,
        // on a partial success we want to map only the rows that were successfully submitted
        actionResult.resultType === ActionResultType.PARTIAL_SUCCESS ? actionResult.submittedRange : undefined
      );
      return Promise.resolve();
    },
    tables: table ? [table] : [],
  });
}

export const createPlatesActionFunction = makeSimpleActionFunction<PlateCreationTableMetadata, {}>({
  label: (params) => getSubmitButtonLabel(params),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreatePlatesActionFunction,
  getDisabled: isTableActionButtonDisabled,
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
